<script setup>
import { useSurveyEntry } from '@/@core/stores/survey-entry'
import SurveyService from '@/services/survey/survey.service'
import AuditEntryPreview from '@/views/incident/audit/EntryPreview.vue'
import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VStepper, VStepperHeader, VStepperItem } from 'vuetify/lib/labs/components.mjs'
import Questions from '@/views/survey/survey-collect.vue/Questions.vue'

definePage({ 
  meta: { 
    layout: 'client', 
    public: true, 
    unauthenticatedOnly: true,
    title: '',
  }, 
})  

const route = useRoute()
const router = useRouter()
const { survey: surveyId,  subscription: subscriptionId, unit: unitId } = route.query

const surveyData = ref()

const participantForm = ref(useSurveyEntry().getFilledSurvey(surveyId)?.participantForm ?? {
  consent: false,
})

const formSteps = ref([])
const currentStep = ref(useSurveyEntry().getFilledSurvey(surveyId)?.currentStep)

await useApi(createUrl(`/public/survey/${surveyId}/form`, {
  query: {
    subscription_id: subscriptionId,
  },
}), {
  beforeFetch: ({ cancel }) => {
    // if(useSurveyEntry().getFilledSurvey(surveyId)?.currentSurvey?.length > 0 && useSurveyEntry().getFilledSurvey(surveyId)?.currentSurvey[0]?.survey_id == atob(surveyId)){
    //   console.log(useSurveyEntry().getFilledSurvey(surveyId)?.currentSurvey)
      
    //   formSteps.value = useSurveyEntry().getFilledSurvey(surveyId)?.currentSurvey
    //   cancel()
    // }
  },
  afterFetch: ({ data }) => {
    // Load Survey Data
    surveyData.value = {
      id: data.id,
      title: data.title,
      description: data.description,
      type: data.type,
      participant: data.participant,
      unit: data.unit,
    }

    document.title = `${data.title} - ${import.meta.env.VITE_APP_NAME}`

    // * Refactor Question & Load Data From LocalStorage If Available
    if (
      useSurveyEntry().getFilledSurvey(surveyId)?.currentSurvey?.length > 0 && 
      useSurveyEntry().getFilledSurvey(surveyId)?.currentSurvey[0]?.survey_id == atob(surveyId)
    ) {
      formSteps.value = useSurveyEntry().getFilledSurvey(surveyId)?.currentSurvey
    } else {
      formSteps.value = data.form_steps.sort((a, b) => a.order - b.order).map(step => {
        const questionGroups = step.question_groups.sort((a, b) => a.order - b.order).map(group => {
          const questions = group.questions.sort((a, b) => a.order - b.order).map(question => {
            const { 
              question_options, 
              ...rest 
            } = question

            return {
              ...rest,
              question_options: question_options,
            }

          })

          return {
            ...group,
            questions: questions,
          }
        })
      
        return {
          ...step,
          questionGroups: questionGroups,
        }
      })
    }
  },
  onFetchError: ({ error }) => {
    if (error?.message === 'SURVEY_ALREADY_SUBMITTED') {
      router.push({ 
        name: 'invalid-url',
        params: { 
          code: 400,
          title: 'Survey Already Submitted!',
          description: 'You have already submitted the survey form.',
        },
      })
    } else {
      router.push({
        name: 'invalid-url',
        params: { code: 404 },
      })
    }
  },
})

const steps = computed(() => {
  return [
    { title: 'Consent' },
    ...formSteps.value.map(step => ({
      title: step.title,
    })),
    { title: 'Review & Submit' },
  ]
})


const refForm = ref([])
const questionAnswers = ref([])

const updateFormData = data => {
  // if (
  //   data.response === null ||
  //   data.response === "" ||
  //   data.response === undefined ||
  //   (Array.isArray(data.response) &&
  //   data.response.length === 0)
  // ) {
  //   return
  // }

  const index = questionAnswers.value.findIndex(
    item => item.question_id === data.question_id,
  )

  if (index > -1) {
    questionAnswers.value[index] = data
  } else {
    questionAnswers.value.push(data)
  }
  formSteps.value[currentStep.value - 1].questionGroups.forEach(group => {
    group.questions.forEach(question => {
      if (question.id === data.question_id) {
        question.response = data.response
      }
    })
  })

  useSurveyEntry().updateCurrentSurvey(formSteps.value, surveyId)
}

const contactForm = ref()

const save = async formIndex => {
  if(currentStep.value == 0){
    if(!subscriptionId){
      contactForm.value.validate().then(({ valid }) => {
        if (!valid) {
          return
        }else{
          currentStep.value++
        }
      })
    }else{
      currentStep.value++
    }
    useSurveyEntry().updateParticipantData(participantForm.value, surveyId)
  }else{
    refForm.value[formIndex - 1].validate().then(({ valid }) => {
      if (!valid) {
        return
      }else{
        currentStep.value++
      }
    })
  }
}

const localLoading = ref(false)

const submitForReview = () => {
  localLoading.value = true

  const responses = formSteps.value.flatMap(step =>
    step.questionGroups.flatMap(group =>
      group.questions.map(question => ({
        question_id: question.id,
        response: question.response?.value 
        ?? (!Array.isArray(question.response) ? question.response : null) 
        ?? null,
        question_option_id: question.response?.id 
        ?? question.response?.value 
        ?? null,
      })),
    ),
  )


  let formData = {}

  if(!subscriptionId){
    formData = {
      unit_id: unitId,
      name: participantForm.value.participant_name ?? null,
      email: participantForm.value.participant_email ?? null,
      phone: participantForm.value.participant_phone ?? null,
      consent: participantForm.value.consent ?? null,
      responses: responses,
    }
  } else {
    formData = {
      subscription_id: subscriptionId,
      responses: responses,
    }
  }

  SurveyService.entry(
    surveyId,
    formData,
    () => {
      localLoading.value = false
      currentStep.value++
    },
    () => {
      localLoading.value = false
    },
  )
}

const stepRefs = ref([])

watch(
  () => currentStep.value,
  value => {
    // console.log('watching currentStep', value)
    useSurveyEntry().updateCurrentStep(currentStep.value, surveyId)

    const el = stepRefs.value[value]?.$el || stepRefs.value[value]

    console.log('el', el)

    el?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" })
  },
)

const computedUnitId = computed(() => {
  if (subscriptionId) {
    return atob(surveyData.value.unit.id)
  } else if (unitId) {
    return atob(unitId)
  } else {
    return null
  }
})

const unit = ref()

await useApi(createUrl('public/units', 
  { 
    query: { 
      search: {
        id: computedUnitId.value,
      },
    },
  },
), {
  beforeFetch: ({ cancel }) => {
    if (!computedUnitId.value) cancel()
  },
  afterFetch: response => {
    if (response?.data?.length === 0) {
      router.push({ name: 'invalid-url', params: { code: 404 } })
    }
    unit.value = response.data[0]
  },
})
</script>

<template>
  <div class="feedback-card feedback-container mx-auto pt-6 pt-sm-10">
    <VCard variant="flat">
      <VCardText class="py-2 px-4 px-sm-6">
        <VStepper
          v-model="currentStep"
          alt-labels
          style="box-shadow: none;"
          class="pa-0 custom-stepper"
        >
          <VStepperHeader>
            <template
              v-for="(step, index) in steps"
              :key="index"
            >
              <VStepperItem
                ref="stepRefs"
                :complete="currentStep > index"
                :title="step.title"
                :subtitle="step.subtitle"
                :value="index + 1"
                color="red"
                complete-color="deep-orange"
                complete-icon="tabler-check"
                class="flex-1 px-0"
              />

              <VDivider
                v-if="index < steps.length - 1"
                class="mx-2 align-self-center"
              />
            </template>
          </VStepperHeader>
        </VStepper>
      </VCardText>

      <VDivider />

      <VWindow
        v-model="currentStep"
        :touch="false"
        style="min-block-size: 400px;"
        class="disable-tab-transition"
        :class="currentStep == steps.length ? 'd-flex align-center justify-center' : ''"
      >
        <VWindowItem class="bg-var-theme-white">
          <VCardText class="px-4 px-sm-6">
            <div class="text-center mb-4">
              <div class="text-h5 text-primary">
                {{ surveyData.title }}
              </div>
              <div class="text-body-2">
                {{ surveyData.description }}
              </div>
            </div>

            <VForm
              v-if="!subscriptionId"
              ref="contactForm"
            >
              <VRow>
                <VCol cols="12">
                  <AppTextField
                    v-model="participantForm.participant_name"
                    label="Name"
                    placeholder="Enter Name"
                    required
                    :rules="[requiredValidator]"
                  />
                </VCol>

                <VCol cols="12">
                  <AppPhoneInput
                    v-model="participantForm.participant_phone"
                    label="Mobile Number"
                    placeholder="Enter Mobile Number"
                    :rules="[requiredValidator]"
                    required
                  />
                </VCol>

                <VCol cols="12">
                  <AppTextField
                    v-model="participantForm.participant_email"
                    label="Email"
                    type="email"
                    placeholder="Enter Email"
                    :rules="[emailValidator]"
                  />
                </VCol>
              </VRow>
            </VForm>
            <div
              v-else
              class="text-body-1"
            >
              <strong>Dear {{ surveyData.participant.name ?? 'User' }}</strong>,<br>
              We'd love to here about your experience. Please take a few moments to share it through this short form.
            </div>

            <VAlert
              variant="tonal"
              color="grey-400"
              class="mt-6"
            >
              <div class="d-flex align-start">
                <VCheckbox
                  v-model="participantForm.consent"
                  color="primary"
                >
                  <template #label>
                    <span class="text-body-2 text-md-body-1">
                      I hereby authorize <strong>{{ unit?.display_name }}</strong> to use this information for public relation effort or for any other lawful purpose.
                      <a
                        v-if="unit?.privacy_policy"
                        :href="unit?.privacy_policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Privacy Policy
                      </a>
                    </span>
                  </template>
                </VCheckbox>
              </div>
            </VAlert>
          </VCardText>
        </VWindowItem>
        <template
          v-for="(step, stepIndex) in formSteps"
          :key="stepIndex"
        >
          <VWindowItem class="bg-var-theme-white">
            <VForm
              :ref="el => refForm[stepIndex] = el"
              ref_for
            >
              <template
                v-for="(group, groupIndex) in step.questionGroups"
                :key="groupIndex"
              >
                <VCardText class="px-4 px-sm-6">
                  <p class="mb-4 text-h5">
                    {{ group.title }}
                  </p>

                  <Questions
                    :questions="group.questions"
                    @update:form-data="updateFormData"
                  />
                </VCardText>
                <VDivider v-if="groupIndex !== step.questionGroups.length - 1" />
              </template>
            </VForm>
          </VWindowItem>
        </template>
        <VWindowItem class="bg-var-theme-white">
          <VCardText class="px-4 px-sm-6">
            <AuditEntryPreview :data="formSteps" />
          </VCardText>
        </VWindowItem>
        <VWindowItem>
          <VCardText class="px-4 px-sm-6 text-center">
            <VIcon
              color="success"
              size="64"
              icon="tabler-circle-dashed-check"
            />
            <h2 class="text-h5 mt-4">
              Thank You!
            </h2>
            <p class="text-body-1 mt-2">
              Survey has been successfully submitted.
            </p>
          </VCardText>
        </VWindowItem>
      </VWindow>

      <!-- Action -->
      <VCardText
        v-if="currentStep !== formSteps.length + 2"
        class="px-4 px-sm-6 d-flex"
        :class="{
          'justify-center': currentStep === 0,
          'justify-space-between': currentStep !== 0,
        }"
      >
        <VBtn
          v-show="currentStep !== 0"
          variant="tonal"
          prepend-icon="tabler-arrow-left"
          @click="currentStep--"
        >
          Back
        </VBtn>

        <VBtn
          v-if="currentStep !== formSteps.length + 1"
          :block="currentStep !== 0 ? false : true"
          color="primary"
          variant="flat"
          append-icon="tabler-arrow-right"
          :disabled="participantForm?.consent === false"
          @click="save(currentStep)"
        >
          {{ currentStep !== 0 ? "Continue" : "Save & Next" }}
        </VBtn>
        <VBtn
          v-else
          color="primary"
          :loading="localLoading"
          @click="submitForReview"
        >
          Submit
        </VBtn>
      </VCardText>
    </VCard>
  </div>
</template>

<style lang="scss">
.v-stepper-item--selected .v-stepper-item__avatar.v-avatar,
.v-stepper-item--complete .v-stepper-item__avatar.v-avatar {
  background-color: #f15921 !important;
}

.v-stepper--alt-labels .v-stepper-item {
  flex-basis: 128px !important;
}

.v-stepper--alt-labels .v-stepper-header {
  box-shadow: none;
  scrollbar-width: none;
}

.feedback-card {
  margin-block: 6rem;
}

.feedback-container {
  max-inline-size: 42rem;
}

@media (max-width: 600px) {
  .feedback-card {
    margin-block-start: 6rem;
  }
}

@media (max-width: 1024px) {
  .feedback-card {
    max-inline-size: 100%;
  }
}
</style>
